/**
 * Utility to filter out test practitioners.
 *
 * Resolution order for whether the filter is ON:
 *   1. REACT_APP_FILTER_TEST_PRACTITIONERS === 'false' -> OFF  (explicit opt-out)
 *   2. REACT_APP_FILTER_TEST_PRACTITIONERS === 'true'  -> ON   (explicit opt-in)
 *   3. No flag set -> filter ON everywhere EXCEPT known non-production hosts
 *      (localhost, staging, preview builds), where test accounts stay visible.
 *
 * Keying the default off the host (rather than relying solely on the env var)
 * means staging keeps showing test practitioners even if the deploy pipeline
 * forgets REACT_APP_FILTER_TEST_PRACTITIONERS=false. The default-ON polarity is
 * preserved: an UNKNOWN host is treated as production and test accounts stay
 * hidden — the safe outcome for the leak we are fixing.
 */

const TEST_PRACTITIONER_EMAILS = [
  'djames012@yahoo.com',
  'sarah.miller.test@mynirva.com',
];

// Hostname fragments that identify a non-production build. Anything matching
// these shows test practitioners by default; everything else is treated as prod.
const NON_PROD_HOST_HINTS = [
  'localhost',
  '127.0.0.1',
  'staging',
  'preview',
  'vercel.app',
  'pages.dev',
];

const isNonProdHost = () => {
  if (typeof window === 'undefined') return false; // SSR/no-window -> treat as prod
  const host = window.location.hostname || '';
  return NON_PROD_HOST_HINTS.some((hint) => host.includes(hint));
};

const shouldFilter = () => {
  const flag = process.env.REACT_APP_FILTER_TEST_PRACTITIONERS;
  if (flag === 'false') return false;
  if (flag === 'true') return true;
  // No explicit flag: filter on prod hosts, show test accounts elsewhere.
  return !isNonProdHost();
};

/**
 * Remove test practitioners from any practitioner list.
 * @param {Array} practitioners – array of practitioner objects (must have an `email` field)
 * @returns {Array} filtered array (or the original value when filter is disabled / input isn't an array)
 */
export const filterTestPractitioners = (practitioners) => {
  if (!shouldFilter()) return practitioners;
  if (!Array.isArray(practitioners)) return practitioners;

  return practitioners.filter(
    (p) => !TEST_PRACTITIONER_EMAILS.includes(p.email?.toLowerCase())
  );
};
